import {NullableString, NullableNumber} from "../../../types/nullable-types";
import {CardExpiration} from "../../../types/card-expiration";
import {Check} from "../../../types/type-checking";
import {formatToParts, Replacement} from "@react-input/mask";
import {MaskPart} from "@react-input/mask/types";
import {IServiceFactory} from "../../../services/service-factory.interface";
import {fullYearFromLastTwoDigitsInCurrentCentury} from "../../../utils/date-time-functions";


interface CardExpirationParseResult {
    value: CardExpiration | null;
    formattedValue: NullableString;
    error: NullableString;
}

const CardExpirationParseNullResult: CardExpirationParseResult = {
    value: null,
    formattedValue: null,
    error: null
}

interface CardExpirationParserOptions {
    mask: string;
    replacement: Replacement;
}

export class CardExpirationParser {
    constructor(private readonly services: IServiceFactory,
                private readonly options: CardExpirationParserOptions) {
    }

    parseValue(value: string): CardExpirationParseResult {
        if(Check.isEmpty(value)) {
            return CardExpirationParseNullResult;
        }

        const maskParts = formatToParts(value, {
            mask: this.options.mask,
            replacement: this.options.replacement,
        });

        if(maskParts.length === 0) {
            return CardExpirationParseNullResult;
        }

        const parts = [
            this._getMonth(maskParts),
            this._getYear(maskParts)
        ]

        if(parts.all(p => Check.isNullOrUndefined(p))) {
            return CardExpirationParseNullResult;
        }

        if(parts.some(p => Check.isNullOrUndefined(p))) {
            return {
                value: null,
                formattedValue: value,
                error: this.services.language.translate('Invalid expiration date')
            };
        }

        const month = parts[0] ?? 0;
        if(month < 1 || month > 12) {
            return {
                value: null,
                formattedValue: value,
                error: this.services.language.translate('Month must be between 01 and 12.')
            };
        }

        const year = fullYearFromLastTwoDigitsInCurrentCentury(parts[1] ?? 0);

        if(year < this.services.time.currentDate.getFullYear()) {
            return {
                value: null,
                formattedValue: value,
                error: this.services.language.translate('Invalid expiration year')
            };
        }

        const cardExpiration = new CardExpiration(month, year);

        return {
            value: cardExpiration,
            error: null,
            formattedValue: cardExpiration.toString()
        }
    }


    private _getMonth(maskParts: MaskPart[]): NullableNumber {
        return this._extractDatePartFromMask(maskParts.slice(0, 2));

    }

    private _getYear(maskParts: MaskPart[]): NullableNumber {
        return this._extractDatePartFromMask(maskParts.slice(3, 5));
    }

    private _extractDatePartFromMask(maskParts: MaskPart[], allowIncompletePart: boolean = false): NullableNumber {

        if(maskParts.length === 0) {
            return null;
        }

        const validParts = maskParts.filter(p => p.type === "input")
            .map(p => p.value);


        if(validParts.length === 0) {
            return null;
        }


        if(!allowIncompletePart && validParts.length !== maskParts.length) {
            return null;
        }

        const value = parseInt(validParts.join(''));
        if(Check.isNumber(value)) {
            return value;
        }

        return null;
    }
}